// src/components/ProductDisplay.tsx
import React from "react";
import {
  Box,
  Container,
  Grid,
  Typography,
  Select,
  MenuItem,
  FormControl,
  Button,
  Stack,
  ButtonBase,
  Menu,
  CircularProgress,
  Alert,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import NorthRoundedIcon from "@mui/icons-material/NorthRounded";

import { useLocation, Link as RouterLink } from "react-router-dom";
import { useQuery } from "@apollo/client";

import { PRODUCTS_LIST_QUERY } from "../graphql/products";
import { buildImageUrl } from "../config";
import ProductIntro from "./productIntro";
import { Helmet } from "react-helmet-async";
import GoogleReviewsSection from "../components/GoogleReviewsSection";
import ListingFaqSection from "../components/ListingFaqSection";

const DESK_CONTROL_H = 54;

/** Shape of product coming from backend */
type BackendProduct = {
  id: string;
  name: string;
  slug?: string | null;
  altText?: string | null;
  customSchema?: string | null;
  productType: string;
  metal?: string | null;
  shortDescription?: string | null;
  longDescription?: string | null;
  price: number;
  galleryImages?: string[] | null;
  rotationImages?: string[] | null;
};

type ProductsListData = {
  publicProducts: BackendProduct[];
  publicProductsCount: number;
};

/** Small, clean product card */
const ProductCard: React.FC<{ p: BackendProduct }> = ({ p }) => {
  const imgPath = p.galleryImages?.[0] || p.rotationImages?.[0] || "";
  const imgSrc = imgPath ? buildImageUrl(imgPath) : "";

  const formatPrice = (value: number) => {
    const a =
      value > 0
        ? `${value.toLocaleString(undefined, { minimumFractionDigits: 0 })}`
        : ``;
    return a;
  };

  return (
    <Box sx={{ textAlign: "center" }}>
      <ButtonBase
        component={RouterLink}
        to={`/product/${p.slug}`}
        state={{ product: p }}
        sx={{
          width: "100%",
          display: "block",
          borderRadius: 0,
          overflow: "hidden",
          backgroundColor: "#fff",
          aspectRatio: "1 / 1",
          position: "relative",
          transition: "transform .25s ease",
          "&:hover": { transform: "translateY(-2px)" },
          "& img": {
            position: "absolute",
            inset: 0,
            margin: "auto",
            maxWidth: { xs: "90%", sm: "88%", md: "82%" },
            maxHeight: { xs: "90%", sm: "88%", md: "82%" },
            objectFit: "contain",
          },
        }}
      >
        {imgSrc && (
          <Box
            component="img"
            loading="lazy"
            decoding="async"
            src={imgSrc}
            alt={p.altText || p.name}
          />
        )}
      </ButtonBase>

      <Box sx={{ mt: 1.5 }}>
        <Typography
          sx={{
            fontFamily: `"Bodoni Moda", serif`,
            fontSize: { xs: "0.95rem", md: "1.05rem" },
            lineHeight: 1.15,
            fontWeight: 500,
          }}
        >
          {p.name}
        </Typography>

        {p.shortDescription && (
          <Typography
            sx={{
              mt: 0.25,
              color: "text.secondary",
              fontSize: { xs: ".78rem", md: ".9rem" },
            }}
          >
            {p.shortDescription}
          </Typography>
        )}

        <Typography sx={{ mt: 0.75, fontSize: { xs: ".85rem", md: "1rem" } }}>
          {formatPrice(p.price)}
        </Typography>
      </Box>
    </Box>
  );
};

const ProductDisplay: React.FC = () => {
  const location = useLocation();

  /** ====== derive product_type & metal from URL ====== */
  const search = new URLSearchParams(location.search);
  const urlTypeRaw = (search.get("type") || "ring").toLowerCase();
  const urlMetalRaw = (search.get("metal") || "").toLowerCase();

  const urlToCanonical: Record<string, string> = {
    ring: "ring",
    rings: "ring",

    necklace: "necklace",
    necklaces: "necklace",

    earring: "ear rings",
    earrings: "ear rings",
    "ear rings": "ear rings",

    bracelet: "bracelets",
    bracelets: "bracelets",

    kara: "karas",
    karas: "karas",

    "bridal-sets": "bridal sets",
    "bridal sets": "bridal sets",
    bridal_sets: "bridal sets",

    "wedding band": "wedding band",
    "wedding-band": "wedding band",
    "wedding bands": "wedding band",
    wedding_bands: "wedding band",

    "best-sellers": "best_sellers",
    best_sellers: "best_sellers",
    "best-seller": "best_sellers",
  };

  const canonicalType = urlToCanonical[urlTypeRaw] || "ring";

  const isBestSellersPage = canonicalType === "best_sellers";

  const productTypeVar = isBestSellersPage ? undefined : canonicalType;

  const bestSellerVar = isBestSellersPage ? true : undefined;

  const headingLabelMap: Record<string, string> = {
    ring: "Rings",
    necklace: "Necklaces & Pendants",
    "ear rings": "Earrings",
    bracelets: "Bracelets",
    karas: "Karas",
    "bridal sets": "Bridal Sets",
    best_sellers: "Best Sellers",
    "wedding band": "Wedding Bands",
  };

  const headingLabel = headingLabelMap[canonicalType] || "Best Sellers";

  const urlToBackendMetal: Record<string, string> = {
    gold: "gold",
    diamond: "diamond",
    "diamond polki": "diamond polki",
    "diamond-polki": "diamond polki",
    diamond_polki: "diamond polki",
    platinum: "platinum",
    palladium: "palladium",
  };

  const backendMetal = urlToBackendMetal[urlMetalRaw] || undefined;

  const [sort, setSort] = React.useState("newest");

  const perPage = 12;

  const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);
  const openSortMenu = Boolean(anchorEl);
  const handleMobileSortClick = (e: React.MouseEvent<HTMLElement>) =>
    setAnchorEl(e.currentTarget);
  const handleMobileSortClose = (value?: string) => {
    setAnchorEl(null);
    if (value) setSort(value);
  };

  const sortToOrder = (value: string): string | null => {
    switch (value) {
      case "newest":
        return "newest";
      case "popular":
        return "best_seller";
      case "name_asc":
        return "name_asc";
      default:
        return null;
    }
  };

  const order = sortToOrder(sort);

  const { data, loading, error, fetchMore } = useQuery<ProductsListData>(
    PRODUCTS_LIST_QUERY,
    {
      variables: {
        bestSeller: bestSellerVar,
        productType: productTypeVar,
        metal: backendMetal,
        offset: 0,
        limit: perPage,
        order,
      },
      fetchPolicy: "cache-and-network",
    },
  );

  const products = data?.publicProducts ?? [];
  const total = data?.publicProductsCount ?? 0;
  const canLoadMore = products.length < total;

  const listingSchema = {
    "@context": "https://schema.org",
    "@type": "ItemList",
    name: headingLabel,
    description: `Browse our ${headingLabel} collection at Haroon Sharif Jewellers`,
    numberOfItems: total,
    itemListElement: products.slice(0, 10).map((p, index) => ({
      "@type": "ListItem",
      position: index + 1,
      name: p.name,
      url: `https://haroonsharifjewellers.com/product/${p.slug}`,
    })),
  };

  const handleViewMore = () => {
    if (!canLoadMore) return;

    fetchMore({
      variables: {
        bestSeller: bestSellerVar,
        productType: productTypeVar,
        metal: backendMetal,
        offset: products.length,
        limit: perPage,
        order,
      },
      updateQuery: (prev, { fetchMoreResult }) => {
        if (!fetchMoreResult) return prev;
        return {
          ...prev,
          publicProducts: [
            ...prev.publicProducts,
            ...fetchMoreResult.publicProducts,
          ],
          publicProductsCount: fetchMoreResult.publicProductsCount,
        };
      },
    });
  };

  const backToTop = () =>
    window.scrollTo({
      top: 0,
      behavior: "smooth",
    });

  const showingFrom = products.length > 0 ? 1 : 0;
  const showingTo = products.length;

  const introType =
    canonicalType === "best_sellers" ? "ring" : (canonicalType as string);

  return (
    <>
      <Helmet>
        <script type="application/ld+json">
          {JSON.stringify(listingSchema)}
        </script>
      </Helmet>
      <Box sx={{ backgroundColor: "#fff", py: { xs: 2, md: 5 } }}>
        <Container maxWidth="xl">
          {/* MOBILE HEADER */}
          <Box
            sx={{
              display: { xs: "block", lg: "none" },
              textAlign: "center",
              mb: 2,
            }}
          >
            <Typography
              sx={{
                fontFamily: `"Bodoni Moda", serif`,
                fontWeight: 700,
                letterSpacing: ".02em",
                fontSize: "1.9rem",
                lineHeight: 1.05,
              }}
            >
              {headingLabel}
            </Typography>
            <Typography
              sx={{
                mt: 0.5,
                fontSize: ".85rem",
                color: "text.secondary",
                letterSpacing: ".06em",
              }}
            >
              {total} Products
            </Typography>
          </Box>

          {/* MOBILE CONTROLS */}
          <Stack
            direction="row"
            alignItems="center"
            justifyContent="space-between"
            sx={{ display: { xs: "flex", lg: "none" }, mb: 2, px: 0.5 }}
          >
            <Button
              size="small"
              onClick={handleMobileSortClick}
              endIcon={<ExpandMoreIcon sx={{ fontSize: 18 }} />}
              sx={{
                color: "inherit",
                textTransform: "none",
                fontSize: ".9rem",
                letterSpacing: ".06em",
                fontWeight: 400,
                px: 0,
                minWidth: 0,
              }}
            >
              Sort by
            </Button>
          </Stack>

          {/* DESKTOP CONTROLS */}
          <Grid
            container
            alignItems="center"
            sx={{ mb: { xs: 2, md: 4 }, display: { xs: "none", lg: "flex" } }}
          >
            {/* LEFT */}
            <Grid item md={4}>
              <Stack direction="row" spacing={3} alignItems="center">
                <FormControl
                  size="small"
                  sx={{
                    minWidth: 240,
                    height: DESK_CONTROL_H,
                    "& .MuiOutlinedInput-root": {
                      height: DESK_CONTROL_H,
                      borderRadius: 0,
                      px: 2,
                      "& fieldset": { borderColor: "rgba(0,0,0,0.2)" },
                      "&:hover fieldset": {
                        borderColor: "rgba(0,0,0,0.36)",
                      },
                    },
                    "& .MuiSelect-select": {
                      display: "flex",
                      alignItems: "center",
                      fontSize: ".92rem",
                      letterSpacing: ".06em",
                      fontWeight: 400,
                    },
                  }}
                >
                  <Select
                    value={sort}
                    onChange={(e) => setSort(String(e.target.value))}
                    IconComponent={ExpandMoreIcon}
                  >
                    <MenuItem value="newest">Newest</MenuItem>
                    <MenuItem value="popular">Most Popular</MenuItem>
                    <MenuItem value="name_asc">Name: A–Z</MenuItem>
                  </Select>
                </FormControl>
              </Stack>
            </Grid>

            {/* CENTER */}
            <Grid item md={4} sx={{ display: "grid", placeItems: "center" }}>
              <Typography
                sx={{
                  fontFamily: `"Bodoni Moda", serif`,
                  fontWeight: 700,
                  letterSpacing: ".02em",
                  fontSize: "2.35rem",
                  lineHeight: 1.05,
                }}
              >
                {headingLabel}
              </Typography>
            </Grid>

            {/* RIGHT */}
            <Grid item md={4}>
              <Stack
                direction="row"
                spacing={3}
                alignItems="center"
                justifyContent="flex-end"
              >
                <Stack direction="row" alignItems="center" spacing={1.5}>
                  <Typography
                    sx={{
                      fontSize: ".92rem",
                      letterSpacing: ".06em",
                      fontWeight: 400,
                    }}
                  >
                    {total} Products
                  </Typography>
                </Stack>
              </Stack>
            </Grid>
          </Grid>

          {/* LOADING / ERROR ABOVE GRID */}
          {loading && products.length === 0 && (
            <Box sx={{ py: 4, textAlign: "center" }}>
              <CircularProgress />
            </Box>
          )}

          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              Failed to load products. Please try again.
            </Alert>
          )}

          {/* PRODUCT GRID */}
          <Grid container spacing={{ xs: 2, sm: 2.5, md: 4 }}>
            {products.map((p) => (
              <Grid key={p.id} item xs={6} sm={6} md={4}>
                <ProductCard p={p} />
              </Grid>
            ))}

            {products.length === 0 && !loading && !error && (
              <Grid item xs={12}>
                <Typography
                  sx={{ textAlign: "center", color: "text.secondary", py: 4 }}
                >
                  No products found for this category yet.
                </Typography>
              </Grid>
            )}
          </Grid>

          {/* MOBILE sort menu */}
          <Menu
            anchorEl={anchorEl}
            open={openSortMenu}
            onClose={() => handleMobileSortClose()}
            anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
          >
            <MenuItem onClick={() => handleMobileSortClose("newest")}>
              Newest
            </MenuItem>
            <MenuItem onClick={() => handleMobileSortClose("popular")}>
              Most Popular
            </MenuItem>
            <MenuItem onClick={() => handleMobileSortClose("name_asc")}>
              Name: A–Z
            </MenuItem>
          </Menu>

          {/* END OF PAGE */}
          <Box
            sx={{
              mt: { xs: 5, md: 8 },
              mb: { xs: 6, md: 10 },
              textAlign: "center",
            }}
          >
            <Typography
              sx={{
                color: "text.primary",
                fontSize: { xs: ".7rem", md: "0.9rem" },
                mb: { xs: 2.5, md: 3 },
              }}
            >
              {total > 0
                ? `Showing ${showingFrom} – ${showingTo} of ${total}`
                : "No products to display"}
            </Typography>

            {/* VIEW MORE */}
            {canLoadMore && (
              <Box sx={{ display: "grid", placeItems: "center" }}>
                <Button
                  variant="outlined"
                  sx={{
                    borderRadius: 0,
                    height: { xs: 56, md: 68 },
                    px: 4,
                    width: { xs: "50%", md: "30%" },
                    maxWidth: 520,
                    borderColor: "rgba(0,0,0,0.2)",
                    color: "text.primary",
                    textTransform: "uppercase",
                    letterSpacing: ".12em",
                    fontSize: { xs: ".67rem", md: "0.7rem" },
                    "&:hover": {
                      borderColor: "rgba(0,0,0,0.36)",
                      backgroundColor: "transparent",
                    },
                  }}
                  onClick={handleViewMore}
                  disabled={loading}
                >
                  View More
                </Button>
              </Box>
            )}

            {/* BACK TO TOP */}
            <Stack
              direction="row"
              alignItems="center"
              justifyContent="center"
              spacing={1.5}
              sx={{ mt: { xs: 3.5, md: 4 } }}
            >
              <Stack alignItems="center" spacing={0.4}>
                <Box sx={{ width: 12, height: 2, bgcolor: "text.primary" }} />
                <NorthRoundedIcon
                  sx={{ fontSize: 12, color: "text.primary" }}
                />
              </Stack>
              <Button
                onClick={backToTop}
                sx={{
                  textTransform: "uppercase",
                  letterSpacing: ".12em",
                  fontSize: { xs: ".67rem", md: ".7rem" },
                  color: "text.primary",
                  px: 0,
                  minWidth: 0,
                  "&:hover": { backgroundColor: "transparent" },
                }}
              >
                Back to Top
              </Button>
            </Stack>
          </Box>
        </Container>
        <Box
          sx={{
            width: "100%",
            borderTop: "1px solid rgba(0,0,0,0.1)",
          }}
        />
      </Box>
      <ProductIntro canonicalType={introType} metal={backendMetal} />
      <GoogleReviewsSection />
      <ListingFaqSection
        productType={canonicalType === "best_sellers" ? "ring" : canonicalType}
        metal={backendMetal}
        headingLabel={headingLabel}
      />
    </>
  );
};

export default ProductDisplay;
